// Grids & Gantts — what a TABLE LINE row and a STOCK CHECK cell look like
// (the rules are ./lines.js and ./stock.js; the wiring is Structure.jsx).
//
//   LineIcon    the small drawing beside a table line (a part on a tray) —
//               a line is not a work item, so it does not wear a work type's icon
//   StockCell   the stock check's chip ("Short 26" red, "OK" green, "3 short"
//               red on every row above), with a hover card that shows the sum:
//               what is needed, what is in stock, what earlier work takes
//               first, what is short — or, on a row above, which parts

import { useEffect, useRef, useState } from 'react';
import { FloatingCard } from './Rescheduler';
import { shortUnder } from './stock';

export function LineIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" fill="none"
      style={{ display: 'inline-block', verticalAlign: 'middle', flex: '0 0 auto' }}>
      <path d="M8 1.8l5.3 3v6.4L8 14.2l-5.3-3V4.8z" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M2.9 4.9L8 7.8l5.1-2.9M8 7.8v6.2" stroke={color} strokeWidth="1.1" strokeLinejoin="round" opacity="0.75" />
    </svg>
  );
}

const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? '—' : (Math.round(Number(n) * 1000) / 1000).toLocaleString('en-US', { maximumFractionDigits: 3 }));
const ownerOf = (k) => String(k || '').split('~')[0];

function Figures({ ui, own }) {
  const rowS = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '2px 0', fontVariantNumeric: 'tabular-nums' };
  return (
    <div style={{ marginTop: 6 }}>
      <div style={rowS}><span>Needed here</span><strong>{fmt(own.need)}</strong></div>
      <div style={rowS}><span>In stock (every storage location)</span><strong>{fmt(own.avail)}</strong></div>
      <div style={rowS}><span>Taken first by work due earlier</span><strong>{fmt(own.before)}</strong></div>
      <div style={{ ...rowS, borderTop: `1px solid ${ui.border}`, marginTop: 3, paddingTop: 5 }}>
        <span style={{ fontWeight: 700 }}>{own.short > 0 ? 'Short' : 'Left after this'}</span>
        <strong style={{ color: own.short > 0 ? ui.red : ui.green }}>{fmt(own.short > 0 ? own.short : own.left)}</strong>
      </div>
      <div style={{ color: ui.muted, fontSize: 11.5, marginTop: 6 }}>
        {own.users > 1
          ? `${own.users} rows on this grid need ${own.part} — ${fmt(own.total)} in all, taken in Due date order (Done work needs nothing).`
          : `Only this row on the grid needs ${own.part}.`}
      </div>
    </div>
  );
}

export function StockCell({ cell, col, row, ctx, ui, style }) {
  const [anchor, setAnchor] = useState(null);
  const shut = useRef(0);
  const open = (el) => { if (shut.current) { clearTimeout(shut.current); shut.current = 0; } setAnchor(el); };
  const close = () => { if (shut.current) clearTimeout(shut.current); shut.current = setTimeout(() => setAnchor(null), 140); };
  useEffect(() => () => { if (shut.current) clearTimeout(shut.current); }, []);
  const tone = cell.band ? (ui[cell.band] || ui.fg) : ui.muted;
  const info = cell.stock || {};
  const own = info.own || null;
  const card = () => {
    if (own && own.made && !info.n) {
      return (
        <>
          <div style={{ fontWeight: 700, fontSize: 12.5 }}>{own.part}</div>
          <div style={{ marginTop: 6 }}>Made on this grid by <strong>{own.made}</strong> — not taken from stock, so it is not judged.</div>
          <div style={{ color: ui.muted, fontSize: 11.5, marginTop: 6 }}>The column can judge such parts too: its menu → “Judge parts the grid makes”.</div>
        </>
      );
    }
    if (own && !(info.n > (own.short > 0 ? 1 : 0))) {
      return (
        <>
          <div style={{ fontWeight: 700, fontSize: 12.5 }}>{own.part}</div>
          <Figures ui={ui} own={own} />
        </>
      );
    }
    // ONE LINE PER PART — what a buyer reads off it: which parts, on how many
    // rows, short by how much in all (worst first).
    const net = typeof ctx.stockOf === 'function' ? ctx.stockOf(col) : null;
    const list = shortUnder(net, row.key, row.countKeys, ctx.linesOf);
    const byPart = new Map();
    list.forEach((x) => {
      const k = String(x.part).toLowerCase();
      const g = byPart.get(k) || { part: x.part, rows: 0, short: 0, avail: x.avail, first: x.key };
      g.rows += 1; g.short += x.short;
      byPart.set(k, g);
    });
    const parts = [...byPart.values()].sort((a, b) => b.short - a.short || String(a.part).localeCompare(String(b.part)));
    const shown = parts.slice(0, 10);
    return (
      <>
        <div style={{ fontWeight: 700, fontSize: 12.5 }}>{list.length} short {list.length === 1 ? 'row' : 'rows'} under {row.it ? row.key : (row.summary || 'this row')}</div>
        <div style={{ color: ui.muted, fontSize: 11.5, marginTop: 2 }}>{parts.length} {parts.length === 1 ? 'part' : 'parts'} — short in all, and what is in stock</div>
        <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '2px 12px', fontVariantNumeric: 'tabular-nums' }}>
          {shown.map((x) => (
            <div key={x.part} style={{ display: 'contents' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong>{x.part}</strong> <span style={{ color: ui.muted }}>· {x.rows === 1 ? ownerOf(x.first) : `${x.rows} rows`}</span>
              </span>
              <span style={{ color: ui.red, fontWeight: 700 }}>short {fmt(x.short)}</span>
              <span style={{ color: ui.muted }}>{fmt(x.avail)} in stock</span>
            </div>
          ))}
        </div>
        {parts.length > shown.length ? <div style={{ color: ui.muted, fontSize: 11.5, marginTop: 4 }}>… and {parts.length - shown.length} more parts — open the row to see them all.</div> : null}
      </>
    );
  };
  return (
    <div style={style}>
      <span onMouseEnter={(e) => open(e.currentTarget)} onMouseLeave={close}
        style={{
          display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontSize: 11.5, fontWeight: 700, padding: '1px 7px', borderRadius: 7, cursor: 'help',
          background: cell.band ? `${tone}1f` : 'transparent', color: tone, border: cell.band ? `1px solid ${tone}44` : 'none',
        }}>{cell.text}</span>
      {anchor && (own || info.n) ? (
        <FloatingCard anchor={anchor} ui={ui} width={300} onEnter={() => open(anchor)} onLeave={close}>{card()}</FloatingCard>
      ) : null}
    </div>
  );
}
